import {
  ApolloLink,
  FetchPolicy,
  NextLink,
  Observable,
  Operation,
  ServerError,
} from '@apollo/client';
import { NetworkError } from '@apollo/client/errors';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import { OperationTypeNode } from 'graphql';
import { set } from 'lodash';

import { redirectToLogin } from '@/components/navigation/navigationUtils';
import { isInBeforeUnloadPhase } from '@/navigation/UnloadPrompt.provider';
import { diagnostics } from '@/utils/diagnostics';
import { getEnvironment } from '@/utils/environmentUtils';

import {
  GraphQLSubscriptionErrors,
  isPureGraphQLError,
  OPERATION_NAME_EXTENSION,
} from './errors';
import { typePolicies } from './typePolicies/typePolicies.generated';

export const forceLoginLink: ApolloLink = onError(({ networkError }) => {
  // if we're on the server, don't do handling here
  if (typeof window === 'undefined') return;

  // We expect 401s to happen when the user isn't logged in, and don't want to notify on them, but we do
  // want to redirect that user to the login page.
  const isUnauthenticatedError =
    networkError &&
    'statusCode' in networkError &&
    networkError.statusCode === 401;
  if (isUnauthenticatedError) {
    return redirectToLogin();
  }
});

// Sentry caps its message length fairly low and won't display anything if the message is too long.
const MESSAGE_LENGTH_LIMIT = 250;
const IGNORED_STATUSES = new Set([401, 403, 404]);

// Network error messages that are expected/normal and should not be reported to Sentry
const IGNORED_NETWORK_ERROR_MESSAGES = new Set([
  'Socket closed', // WebSocket reconnection - normal behavior
  // The backend caps each subscription at a fixed lifetime and sends this
  // signal when the client must restart it (see isReconnectSubscriptionError).
  GraphQLSubscriptionErrors.RECONNECT_MESSAGE,
]);

// When a request never reaches the server (navigation away, tab close, offline,
// laptop sleep/resume, dead preview-env backend), fetch() rejects with one of
// these messages. There's no status code or server trace to act on, so we don't
// report them as errors; logErrorDetailsLink still records an info-level
// breadcrumb. Server failures that produce a response (e.g. 422/5xx) arrive as
// ServerError with a statusCode and are still reported.
// Matched by prefix because Chrome 141+ appends the host, e.g.
// "Failed to fetch (tenant.withluminary.com)".
const UNACTIONABLE_FETCH_FAILURE_PREFIXES = [
  'Failed to fetch', // Chrome
  'NetworkError when attempting to fetch resource', // Firefox
  'Load failed', // Safari
  'The operation was aborted', // request aborted before a response
  'The user aborted a request',
  'signal is aborted',
];

function isUnactionableFetchFailure(networkError: NetworkError): boolean {
  // Errors with a statusCode got a server response and may be actionable
  if (!networkError || 'statusCode' in networkError) return false;
  return UNACTIONABLE_FETCH_FAILURE_PREFIXES.some((prefix) =>
    networkError.message.startsWith(prefix)
  );
}

interface GraphQLNetworkErrorGrouping {
  failureKind: string;
  fingerprint: string[];
  titleLabel: string;
}

const URL_IN_ERROR_MESSAGE = /https?:\/\/[^\s)]+/g;

/**
 * Groups network errors by stable transport details instead of request URLs.
 * The raw message remains in Sentry context, but tenant-specific hosts do not
 * create a separate issue for each test run or customer.
 */
export function getGraphQLNetworkErrorGrouping(
  networkError: NonNullable<NetworkError>,
  operationName: string,
  { redactMessage = false }: { redactMessage?: boolean } = {}
): GraphQLNetworkErrorGrouping {
  const statusCode =
    'statusCode' in networkError && typeof networkError.statusCode === 'number'
      ? networkError.statusCode
      : undefined;

  let failureKind: string;
  let titleLabel: string;

  if (statusCode !== undefined) {
    failureKind = `status:${statusCode}`;
    titleLabel = `HTTP ${statusCode}`;
  } else {
    const code =
      'code' in networkError && typeof networkError.code === 'string'
        ? networkError.code
        : undefined;
    const errno =
      'errno' in networkError && typeof networkError.errno === 'string'
        ? networkError.errno
        : undefined;
    const transportCode = code ?? errno;

    if (transportCode) {
      failureKind = `code:${transportCode}`;
      titleLabel = transportCode;
    } else {
      const normalizedMessage = redactMessage
        ? 'redacted'
        : networkError.message.replace(URL_IN_ERROR_MESSAGE, '<url>');
      failureKind = `message:${normalizedMessage}`;
      titleLabel = networkError.name || 'unknown';
    }
  }

  return {
    failureKind,
    fingerprint: [
      'graphql-network',
      operationName,
      networkError.name || 'unknown',
      failureKind,
    ],
    titleLabel,
  };
}

// GraphQL operations that should not report network errors to Sentry
const IGNORED_OPERATIONS = new Set([
  'GetHouseholdMissingDocuments', // Expected to fail in some scenarios - https://luminary.sentry.io/issues/6865248703/
  'FetchCurrentUserWorkflowTrackerHistory',
  'FetchHouseholdWorkflowTrackerHistory',
]);

/**
 * Get the URL for a Honeycomb trace from a network error.
 */
function getTraceUrl(networkError: NetworkError) {
  const env = getEnvironment();
  // there are different namings for the environments in Honeycomb, but we need them to be able to
  // link to the correct environment in the UI
  const honeycombEnv = (() => {
    switch (env) {
      case 'development':
        return 'local';
      case 'staging':
        return 'staging';
      case 'prod':
        return 'prod';
      default:
        diagnostics.error(
          'Unknown environment for trace URL',
          new Error('Unknown environment'),
          { env }
        );
        return null;
    }
  })();

  const traceId = (networkError as ServerError)?.response?.headers.get(
    'x-lum-tr-id'
  );
  if (!traceId || !honeycombEnv) {
    return undefined;
  }

  return `https://ui.honeycomb.io/luminary/environments/${honeycombEnv}/datasets/platform/trace/${traceId}`;
}

export const reportNetworkErrorsLink: ApolloLink = onError(
  ({ operation, networkError, graphQLErrors: gqlErrors }) => {
    if (networkError) {
      const statusCode =
        'statusCode' in networkError ? networkError.statusCode : undefined;
      const isIgnoredStatusCode =
        statusCode && IGNORED_STATUSES.has(statusCode);
      const isIgnoredErrorMessage = IGNORED_NETWORK_ERROR_MESSAGES.has(
        networkError.message
      );
      const isIgnoredOperation =
        operation.operationName &&
        IGNORED_OPERATIONS.has(operation.operationName);
      // GraphQLWsLink delivers application errors through the network channel.
      // Opted-in callers own recovery and reporting for those errors only.
      const callerReportsGraphQLErrors =
        operation.getContext().graphQLErrorReporting === 'caller' &&
        isPureGraphQLError(networkError);
      const isSensitive = operation.getContext().sensitive;
      const grouping = getGraphQLNetworkErrorGrouping(
        networkError,
        operation.operationName,
        { redactMessage: isSensitive }
      );

      if (
        isIgnoredStatusCode ||
        isInBeforeUnloadPhase ||
        isIgnoredErrorMessage ||
        isIgnoredOperation ||
        callerReportsGraphQLErrors ||
        isUnactionableFetchFailure(networkError)
      ) {
        // Do not report 401s, errors during onbeforeunload, expected network errors, or ignored operations
        return; // Returning undefined here will propagate the error to the next link
      } else {
        let stringifiedErrorObj: string | undefined = undefined;
        try {
          // if the statusCode is missing, then it's a plain error object -- log the entire thing to provide some more context
          stringifiedErrorObj = !('statusCode' in networkError)
            ? JSON.stringify(networkError).slice(0, MESSAGE_LENGTH_LIMIT)
            : undefined;
        } catch (e) {
          stringifiedErrorObj = 'Error stringifying network error';
        }

        let graphQLErrors: string | undefined = undefined;
        try {
          if (gqlErrors) {
            graphQLErrors = JSON.stringify(gqlErrors).slice(
              0,
              MESSAGE_LENGTH_LIMIT
            );
          }
        } catch (e) {
          graphQLErrors = 'Error stringifying GraphQL errors';
        }

        diagnostics.error(
          `[Network error: ${grouping.titleLabel}]`,
          isSensitive
            ? new Error('Sensitive GraphQL operation failed')
            : networkError,
          // extra data to send to Sentry
          isSensitive
            ? {
                failureKind: grouping.failureKind,
                operationName: operation.operationName,
                statusCode,
                traceUrl: getTraceUrl(networkError),
              }
            : {
                failureKind: grouping.failureKind,
                name: networkError.name,
                message: networkError.message,
                operationName: operation.operationName,
                statusCode,
                stringifiedErrorObj,
                graphQLErrors,
                traceUrl: getTraceUrl(networkError),
                query: JSON.stringify(operation.query),
                variables: JSON.stringify(operation.variables),
                extensions: JSON.stringify(operation.extensions),
              },
          // fingerprint to aggregate on in Sentry
          grouping.fingerprint
        );
      }
    }
  }
);

/**
 * Stamps the operation name onto each GraphQL error in a response.
 *
 * `ApolloError.graphQLErrors` holds the same error objects that the link chain
 * sees (Apollo copies the array, not the errors), so anything added here is
 * visible to `diagnostics.error` at the call site. That lets Sentry group
 * GraphQL errors by operation instead of only by response path, which is
 * shared across unrelated queries (e.g. the `paginatedData` alias).
 */
export const annotateGraphQLErrorsLink: ApolloLink = new ApolloLink(
  (operation: Operation, forward: NextLink) => {
    return forward(operation).map((response) => {
      response.errors?.forEach((error) => {
        // GraphQLFormattedError types `extensions` as readonly, but these are
        // plain objects parsed from the HTTP response, so writing is safe.
        const extensions: Record<string, unknown> = {
          ...error.extensions,
          [OPERATION_NAME_EXTENSION]: operation.operationName,
        };
        Object.assign(error, { extensions });
      });

      return response;
    });
  }
);

export const logErrorDetailsLink: ApolloLink = onError(
  ({ operation, networkError, graphQLErrors, response }) => {
    // Credential operations must not attach variables, response bodies, or
    // upstream error messages to diagnostics, including validation failures.
    if (operation.getContext().sensitive) {
      diagnostics.info('Sensitive GraphQL operation failed', {
        operationName: operation.operationName,
      });
      return;
    }
    const errCtx = {
      operationName: operation.operationName,
      query: JSON.stringify(operation.query),
      variables: JSON.stringify(operation.variables),
      extensions: JSON.stringify(operation.extensions),
    };

    if (response) {
      diagnostics.info(`[INFO][Response]`, {
        ...errCtx,
        response: JSON.stringify(response),
      });
    }

    if (networkError) {
      diagnostics.info(`[INFO][Network error]`, {
        ...errCtx,
        error: networkError,
        name: networkError.name,
        message: networkError.message,
      });
    }

    if (graphQLErrors) {
      graphQLErrors.forEach((graphQLError) => {
        diagnostics.info(`[INFO][GraphQL error]`, {
          ...errCtx,
          error: graphQLError,
          message: graphQLError.message,
          path: JSON.stringify(graphQLError.path),
        });
      });
    }
  }
);

function isSubscriptionOperation(operation: Operation): boolean {
  const mainDefinition = getMainDefinition(operation.query);
  return (
    'operation' in mainDefinition &&
    mainDefinition.operation === OperationTypeNode.SUBSCRIPTION
  );
}

// This link keeps GraphQL subscriptions off __print routes. A PDF render is a
// snapshot, so no print page needs live updates. The WebSocket and the
// backend's routine "reconnect subscription" signal only add network activity
// and Apollo errors that can delay or fail print readiness. The returned
// Observable never emits and never completes, so consumers stay in the same
// pending state as while a socket connects, and graphql-ws never opens one.
export const printSubscriptionBlocker: ApolloLink = new ApolloLink(
  (operation, forward) => {
    if (typeof window === 'undefined') return forward(operation);

    const isPrint = window.location.pathname.includes('__print');

    if (!isPrint || !isSubscriptionOperation(operation)) {
      return forward(operation);
    }

    return new Observable(() => {});
  }
);

// This link is used to monitor the number of GraphQL requests that
// are currently active. It only works on __print routes.
export const printNetworkStatusMonitor: ApolloLink = new ApolloLink(
  (operation, forward) => {
    if (typeof window === 'undefined') return forward(operation);

    const isPrint = window.location.pathname.includes('__print');

    if (!isPrint) return forward(operation);

    // ignore subscriptions for the purposes of tracking network status
    if (isSubscriptionOperation(operation)) return forward(operation);

    const observable = forward(operation);
    return new Observable((observer) => {
      set(
        window,
        '__print.inFlightRequests',
        (window.__print?.inFlightRequests || 0) + 1
      );

      const subscription = observable.subscribe(observer);
      // Observable cleanup runs once on completion, error, or cancellation.
      // A response does not necessarily end the request.
      return () => {
        subscription.unsubscribe();
        set(
          window,
          '__print.inFlightRequests',
          (window.__print?.inFlightRequests || 0) - 1
        );
      };
    });
  }
);

// This link is used to monitor the count of Apollo errors.
// It only works on __print routes.
export const printApolloErrorsMonitor: ApolloLink = onError(
  ({ networkError, graphQLErrors }) => {
    if (typeof window === 'undefined') return;

    const isPrint = window.location.pathname.includes('__print');

    if (!isPrint) return; // No need to forward() here, as that will retry the request in onError

    if (networkError || graphQLErrors) {
      set(
        window,
        '__print.apolloErrorCount',
        (window.__print?.apolloErrorCount || 0) + 1
      );
    }
  }
);

/**
 * Recursively processes GraphQL response data to transform scalar values based on type policies.
 *
 * This function mimics Apollo Client's type policy read functions for scalar fields outside
 * of the cache, applying the same transformations to network-only or no-cache responses.
 * It walks through the response data, identifies fields with defined type policies containing
 * read functions, and applies those read functions to convert string values into their proper
 * JavaScript types (like Date objects or Decimal values).
 *
 * This is particularly used by the scalarParsingLink to ensure consistent data types are returned
 * regardless of fetch policy.
 *
 * @param data - The GraphQL response data to process
 * @param parentType - Optional typename for nested objects that don't have their own __typename
 * @returns The processed data with scalar values properly transformed
 */
export function parseScalars(
  data: Record<string, unknown>,
  parentType?: string
): unknown {
  // Handle arrays by recursively processing each item
  if (Array.isArray(data)) {
    return data.map((item) =>
      parseScalars(item as Record<string, unknown>, parentType)
    );
  }

  if (typeof data === 'object' && data !== null) {
    // Get the typename from the data or fall back to parent's type for nested objects
    // This is important because nested objects might not have __typename fields
    const typename = (data.__typename as string) ?? parentType ?? 'Query';
    const parsed: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(data)) {
      // Look up if this field has a type policy in our generated type policies
      // This complex access path handles the structure of the typePolicies object safely
      const fieldPolicy =
        typename &&
        (
          (typePolicies as Record<string, unknown>)?.[typename] as {
            fields: Record<string, unknown>;
          }
        )?.fields?.[key];

      if (
        fieldPolicy &&
        typeof fieldPolicy === 'object' &&
        'read' in fieldPolicy
      ) {
        // Field has a read function in its type policy (typically for custom scalars)
        // Apply the same transformation that would happen in the Apollo cache
        parsed[key] = (
          fieldPolicy as { read: (value: unknown) => unknown }
        ).read(value);
      } else if (typeof value === 'object' && value !== null) {
        // For nested objects that don't have a direct read function,
        // recursively process them and pass down the current typename for context
        parsed[key] = parseScalars(value as Record<string, unknown>, typename);
      } else {
        // For primitive values with no type policy, keep as is
        parsed[key] = value;
      }
    }

    return parsed;
  }

  // For non-object, non-array values (primitives), return as is
  return data;
}

/**
 * Apollo Link that processes scalar values in GraphQL responses when using no-cache or network-only fetch policies, mutations, or subscriptions.
 *
 * Apollo Client normally applies type policies with read functions only to data that goes through the cache.
 * When using fetch policies like 'no-cache' or 'network-only', the data bypasses this processing.
 * Mutations and subscriptions also bypass the cache's scalar transformations.
 * This link ensures scalar values (like dates, currency amounts, percentages) are properly transformed
 * from string representations to their corresponding JavaScript objects (Date, Decimal, etc.) regardless
 * of fetch policy or operation type, providing consistency across the application.
 *
 * Note: We only apply this transformation for 'no-cache', 'network-only', mutations, and subscriptions
 * because other policies (like 'cache-first' or 'cache-and-network') will pass the data through Apollo's cache,
 * where the type policies' read functions will already be applied. Applying the transformations twice
 * would be inefficient and unnecessary since the data would already be in the correct format.
 *
 * It uses the parseScalars function to walk through the response data and apply the appropriate
 * transformations based on the typePolicies defined in the application.
 */
export const scalarParsingLink = new ApolloLink(
  (operation: Operation, forward: NextLink) => {
    const context = operation.getContext();
    const fetchPolicy = context.fetchPolicy as FetchPolicy;
    const isLazy = context.isLazy as boolean;

    // forceParseScalars is used to force the parsing of scalars even
    // if the fetch policy is not 'no-cache', 'network-only', or 'lazy'. We use this for
    // e.g. fetchMore queries, which seem to avoid default scalar parsing.
    const forceParseScalars = context.forceParseScalars as boolean;
    const mainDefinition = getMainDefinition(operation.query);
    let operationKind: OperationTypeNode | null = null;

    if ('operation' in mainDefinition) {
      operationKind = mainDefinition.operation;
    }

    const isMutation = operationKind === OperationTypeNode.MUTATION;
    const isSubscription = operationKind === OperationTypeNode.SUBSCRIPTION;

    return forward(operation).map((response) => {
      if (
        fetchPolicy === 'no-cache' ||
        fetchPolicy === 'network-only' ||
        forceParseScalars ||
        isLazy ||
        isMutation ||
        isSubscription
      ) {
        if (response?.data) {
          response.data = parseScalars(response.data) as typeof response.data;
        }
      }

      return response;
    });
  }
);
